import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, Car, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { adminVehiclesAPI, uploadsAPI } from '../services/api';

// Admin: realistic model picture (any image → AI cut-out → 250×250 transparent PNG)
const VehicleModelImageUpload = ({ brand, model, image, onChanged }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await adminVehiclesAPI.uploadImage(brand, model, file);
      toast.success(`Image détourée et recadrée en ${r.width}×${r.height} PNG transparent`);
      onChanged?.(r.image);
    } catch (e) { toast.error(e.response?.data?.detail || "Traitement de l'image impossible"); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (image && !window.confirm('Générer une nouvelle photo par IA et remplacer l\'actuelle ?')) return;
    setBusy(true);
    try {
      const r = await adminVehiclesAPI.aiImageRegenerate(brand, model);
      toast.success('Nouvelle photo générée par IA (250×250 PNG transparent)');
      onChanged?.(r.image);
    } catch (e) { toast.error(e.response?.data?.detail || 'Génération impossible'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer l'image de ce modèle ?")) return;
    setBusy(true);
    try { await adminVehiclesAPI.deleteImage(brand, model); toast.success('Image supprimée'); onChanged?.(null); } catch { toast.error('Suppression impossible'); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid="vehicle-model-image-block">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files?.[0]); }}
        onClick={() => !busy && input.current?.click()} role="button" tabIndex={0}
        data-testid="vehicle-model-image-drop"
        className={`w-[76px] h-[76px] rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer bg-white ${drag ? 'border-orange-500' : 'border-gray-300 hover:border-orange-400'}`}
        title="Déposer ou cliquer pour choisir une photo du modèle">
        {busy ? <Loader2 className="animate-spin text-orange-500" size={22} /> : image ? <img src={uploadsAPI.resolveUrl(image)} alt={model} className="w-full h-full object-contain" data-testid="vehicle-model-image-preview" /> : <Car className="text-gray-300" size={26} />}
      </div>
      <div className="text-xs text-gray-600 space-y-1">
        <p className="font-medium text-gray-800">Image du modèle</p>
        <p>Toute photo acceptée → fond retiré par IA, recadrage auto, <strong>250×250 PNG transparent</strong>.</p>
        <div className="flex gap-2 pt-0.5">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid="vehicle-model-image-choose"><ImagePlus size={13} className="mr-1" /> {image ? 'Remplacer' : 'Choisir une photo'}</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={regenerate} className="text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="vehicle-model-image-regenerate" title="Photo réaliste générée par IA (~30 s)"><Sparkles size={13} className="mr-1" /> {image ? 'Regénérer (IA)' : 'Générer (IA)'}</Button>
          {image && <Button size="sm" variant="outline" disabled={busy} onClick={remove} className="text-red-600" data-testid="vehicle-model-image-delete"><Trash2 size={13} className="mr-1" /> Retirer</Button>}
        </div>
      </div>
      <input ref={input} type="file" accept="image/*" className="hidden" data-testid="vehicle-model-image-input" onChange={(e) => { send(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
};

export default VehicleModelImageUpload;
